import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { DirectionalHint, FontIcon, FontSizes, FontWeights, Icon, IIconProps, IIconStyleProps, IIconStyles, ISearchBoxStyles, IStackItemStyles, IStackTokens, IStyleFunctionOrObject, ITextStyles, ITooltipHostStyles, mergeStyles, SearchBox, Stack, Text, TooltipHost } from "@fluentui/react";
import Location from "../../src/taskpane/components/directoryComponents/Locations";
import SharePointSite from "../../src/taskpane/components/directoryComponents/sharepoint/SharePointSite";
import Teams from "../../src/taskpane/components/directoryComponents/teams/Teams";
import { ContentManagerDirectoryEnum } from "../../src/taskpane/components/enums/ContentManagerDirectoryEnum";
import { IAuthTokenResultDto } from "../../src/taskpane/interfaces/IAuthTokenResultDto";
import { RootState } from "../../src/taskpane/redux";
import { resetContentManagerDirectory, setContentManagerFilterValue } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { resetDuplicateFilesAndOverWriteSettings } from "../../src/taskpane/redux/modules/file";
import { resetLocationBeforeSaving } from "../../src/taskpane/redux/modules/oneDrive";
import { setIsSavingInBackground } from "../../src/taskpane/redux/modules/savingInBackground";
import { resetSavingLocation } from "../../src/taskpane/redux/modules/savingLocation";
import { setSearch } from "../../src/taskpane/redux/modules/search";
import { resetWorkingDirectories } from "../../src/taskpane/redux/modules/workingDirectory";
import ExchangeDirectory from "./Exchange/ExchangeDirectory";
import TessAccounts from "../../src/taskpane/components/directoryComponents/Tess/TessAccounts";
import { useTessPopUpAuth } from "../Hooks/useTessPopUpAuth";

const headerStackTokens: IStackTokens = { childrenGap: 10 };

const filterIcon: IIconProps = { iconName: 'Filter' };

const filterBoxStyles: Partial<ISearchBoxStyles> = {
    root: { width: "100%", borderColor: "#c8c6c4", borderRadius: 0 }
};

const tooltipDisplayFlexStyle: Partial<ITooltipHostStyles> = {
    root: { display: "flex" }
};

const textStyles: ITextStyles = {
    root: {
        display: "flex",
        marginLeft: 10,
    },
};

const textRegular = mergeStyles({
    fontSize: FontSizes.size18,
    fontWeight: FontWeights.semibold,
});

const menuIconStyles: IStyleFunctionOrObject<IIconStyleProps, IIconStyles> = {
    root: {
        fontSize: 16,
        marginLeft: -5,
        paddingBottom: 10,
        // paddingTop: 7,
        // paddingRight: 15,
        selectors: {
            ":hover": { cursor: 'pointer' }
        }
    }
}

const mapDispatchToProps = {
    resetContentManagerDirectory,
    setContentManagerFilterValue,
    resetWorkingDirectories,
    resetDuplicateFilesAndOverWriteSettings,
    resetSavingLocation,
    setIsSavingInBackground,
    resetLocationBeforeSaving,
    setSearch
};

const mapStateToProps = (state: RootState) => {
    return {
        contentManagerDirectory: state.contentManagerDirectory.directory,
        isSideMenuCollapsed: state.contentManagerDirectory.isSideMenuCollapsed,
        files: state.files.files,
        isTessEnabled: state.tenantInfo?.isTessEnabled
    };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const directoryContainerStackStyles: IStackItemStyles = {
    root: {
        height: "100%",
    }
};

export interface IContentBodyDirectoryProps extends PropsFromRedux {
    requestFlowResourceToken?: (loginCallback: (authResult: IAuthTokenResultDto) => Promise<void>) => Promise<void>;
}

const ContentBodyDirectory: React.FunctionComponent<IContentBodyDirectoryProps> = ({
    resetContentManagerDirectory,
    contentManagerDirectory,
    isSideMenuCollapsed,
    setContentManagerFilterValue,
    requestFlowResourceToken,
    resetWorkingDirectories,
    resetDuplicateFilesAndOverWriteSettings,
    resetSavingLocation,
    setIsSavingInBackground,
    resetLocationBeforeSaving,
    setSearch,
    files,
    isTessEnabled
}: IContentBodyDirectoryProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const tessAuthHook = useTessPopUpAuth(isTessEnabled);

    const filter = React.useCallback((filterText: any) => {
        setContentManagerFilterValue(filterText?.trim());
    }, []);

    const clear = React.useCallback(() => {
        setContentManagerFilterValue("");
    }, []);

    const goBack = React.useCallback(() => {
        resetContentManagerDirectory();
    }, []);

    const goHome = React.useCallback(() => {
        resetWorkingDirectories();
        resetDuplicateFilesAndOverWriteSettings(files);
        resetSavingLocation();
        setIsSavingInBackground(false);
        resetLocationBeforeSaving();
        setSearch("");
        resetContentManagerDirectory();
    }, [files]);

    const filterBox = React.useMemo(() =>
        <SearchBox
            styles={filterBoxStyles}
            placeholder="Filter"
            iconProps={filterIcon}
            onSearch={filter}
            onClear={clear}
        />, []);

    return (
        <Stack styles={directoryContainerStackStyles}>
            {contentManagerDirectory !== ContentManagerDirectoryEnum.Home && !isSideMenuCollapsed && (
                <>
                    <div style={{ paddingLeft: 10, paddingRight: 10, paddingBottom: 5 }}>
                        <Stack horizontal verticalAlign="center" tokens={headerStackTokens}>
                            <TooltipHost content={languageStrings.Back} styles={tooltipDisplayFlexStyle} directionalHint={DirectionalHint.bottomAutoEdge}>
                                <FontIcon iconName="Back" className="xb-navigation-link" onClick={goBack} />
                            </TooltipHost>
                            <Text className={textRegular} styles={textStyles}>
                                {contentManagerDirectory === ContentManagerDirectoryEnum.SharePoint && "SharePoint"}
                                {contentManagerDirectory === ContentManagerDirectoryEnum.Teams && "Teams"}
                                {contentManagerDirectory === ContentManagerDirectoryEnum.Exchange && "Exchange"}
                                {contentManagerDirectory === ContentManagerDirectoryEnum.Tess && "Tess"}
                            </Text>
                        </Stack>
                    </div>
                    <div className="xb-navigation-container">
                        <Stack horizontal>
                            <Stack.Item grow={1}>
                                {filterBox}
                            </Stack.Item>
                        </Stack>
                    </div>
                </>
            )}
            <div className="xb-content-container">
                <div className="xb-content margin-right-0">
                    {isSideMenuCollapsed && <Stack.Item>
                        <Icon iconName="Home" styles={menuIconStyles} onClick={goHome} />
                    </Stack.Item>}
                    {/* {contentManagerDirectory === ContentManagerDirectoryEnum.Home && <Location requestFlowResourceToken={requestFlowResourceToken} />} */}
                    <Location hide={!isSideMenuCollapsed && contentManagerDirectory !== ContentManagerDirectoryEnum.Home} requestFlowResourceToken={requestFlowResourceToken} tessAuthHook={tessAuthHook} />
                    {/* {!isSideMenuCollapsed && contentManagerDirectory === ContentManagerDirectoryEnum.SharePoint && <SharePointSite />} */}
                    <SharePointSite hide={isSideMenuCollapsed || contentManagerDirectory !== ContentManagerDirectoryEnum.SharePoint} />
                    {/* {!isSideMenuCollapsed && contentManagerDirectory === ContentManagerDirectoryEnum.Teams && <Teams />} */}
                    <Teams hide={isSideMenuCollapsed || contentManagerDirectory !== ContentManagerDirectoryEnum.Teams} />
                    {isTessEnabled && tessAuthHook?.isTessTokenAvailable && <TessAccounts hide={isSideMenuCollapsed || contentManagerDirectory !== ContentManagerDirectoryEnum.Tess} tessAuthHook={tessAuthHook} />}
                    {!isSideMenuCollapsed && contentManagerDirectory === ContentManagerDirectoryEnum.Exchange && <ExchangeDirectory />}
                </div>
            </div>
        </Stack>
    )
};

export default React.memo(connector(ContentBodyDirectory));